import type { Monaco } from '@monaco-editor/react';
import type { editor, languages, IDisposable, Position, CancellationToken } from 'monaco-editor';
import {
  extractReferences,
  findReferenceAtPosition,
  resolveReference,
  Reference,
} from '@/utils/referenceUtils';

// ============================================================================
// Types
// ============================================================================

type OpenTabFunction = (itemId: string) => Promise<void>;

interface ProviderRegistration {
  dispose: () => void;
}

// ============================================================================
// Document Link Provider - Makes references clickable (Ctrl+Click)
// ============================================================================

function createDocumentLinkProvider(
  monaco: Monaco,
  openTab: OpenTabFunction
): languages.DocumentLinkProvider {
  return {
    provideLinks: async (
      model: editor.ITextModel,
      token: CancellationToken
    ): Promise<languages.ILinksList | null> => {
      const content = model.getValue();
      const references = extractReferences(content);

      if (references.length === 0) {
        return null;
      }

      // Resolve all references in parallel
      const links: languages.ILink[] = [];

      await Promise.all(
        references.map(async (ref) => {
          if (token.isCancellationRequested) return;

          const resolved = await resolveReference(ref.type, ref.path);

          links.push({
            range: new monaco.Range(
              ref.range.startLine,
              ref.range.startColumn,
              ref.range.endLine,
              ref.range.endColumn
            ),
            url: resolved ? `editor://open/${resolved.id}` : undefined,
            tooltip: resolved
              ? `Open ${ref.type}: ${resolved.name}`
              : `${ref.type} not found: ${ref.path}`,
          });
        })
      );

      return {
        links,
        dispose: () => {},
      };
    },

    resolveLink: async (
      link: languages.ILink,
      token: CancellationToken
    ): Promise<languages.ILink | null> => {
      if (token.isCancellationRequested) return null;

      // Handle our custom URL scheme
      if (link.url && typeof link.url === 'string' && link.url.startsWith('editor://open/')) {
        const itemId = link.url.replace('editor://open/', '');
        try {
          await openTab(itemId);
        } catch (error) {
          console.error('Failed to open tab from link:', error);
        }
        // Return null to prevent Monaco from navigating to the URL
        return null;
      }

      return link;
    },
  };
}

// ============================================================================
// Hover Provider - Shows content preview on hover
// ============================================================================

function createHoverProvider(monaco: Monaco): languages.HoverProvider {
  return {
    provideHover: async (
      model: editor.ITextModel,
      position: Position,
      token: CancellationToken
    ): Promise<languages.Hover | null> => {
      const content = model.getValue();
      const references = extractReferences(content);

      // Find reference at cursor position
      const ref = findReferenceAtPosition(
        references,
        position.lineNumber,
        position.column
      );

      if (!ref) return null;
      if (token.isCancellationRequested) return null;

      // Resolve the reference
      const resolved = await resolveReference(ref.type, ref.path, {
        includeContent: true,
      });

      if (token.isCancellationRequested) return null;

      const range = new monaco.Range(
        ref.range.startLine,
        ref.range.startColumn,
        ref.range.endLine,
        ref.range.endColumn
      );

      if (!resolved) {
        return {
          range,
          contents: [
            { value: `**Not found:** ${ref.type} "${ref.path}"` },
            { value: '_Item may not exist or may have a different name_' },
          ],
        };
      }

      // Build preview content
      const preview = resolved.content
        ? resolved.content.split('\n').slice(0, 20).join('\n')
        : '_Content not available_';

      const truncated = resolved.content && resolved.content.split('\n').length > 20;

      return {
        range,
        contents: [
          { value: `**${ref.type}:** ${resolved.name}` },
          { value: '---' },
          { value: '```yaml\n' + preview + (truncated ? '\n...' : '') + '\n```' },
        ],
      };
    },
  };
}

// ============================================================================
// Definition Provider - F12 Go-to-Definition
// ============================================================================

function createDefinitionProvider(
  monaco: Monaco,
  openTab: OpenTabFunction
): languages.DefinitionProvider {
  return {
    provideDefinition: async (
      model: editor.ITextModel,
      position: Position,
      token: CancellationToken
    ): Promise<languages.Definition | null> => {
      const content = model.getValue();
      const references = extractReferences(content);

      // Find reference at cursor position
      const ref = findReferenceAtPosition(
        references,
        position.lineNumber,
        position.column
      );

      if (!ref) return null;
      if (token.isCancellationRequested) return null;

      // Resolve the reference
      const resolved = await resolveReference(ref.type, ref.path);

      if (!resolved) return null;
      if (token.isCancellationRequested) return null;

      // Open the file in a new tab
      try {
        await openTab(resolved.id);
      } catch (error) {
        console.error('Failed to open tab from definition:', error);
        return null;
      }

      // Return a location pointing to the target file
      // This creates a proper navigation that Monaco will handle
      return {
        uri: monaco.Uri.parse(`inmemory://tab/${resolved.id}`),
        range: new monaco.Range(1, 1, 1, 1),
      };
    },
  };
}

// ============================================================================
// Registration
// ============================================================================

/**
 * Register all reference providers for a Monaco instance
 *
 * @param monaco - The Monaco instance
 * @param openTab - Function to open a tab by item ID
 * @param language - The language to register for (default: 'markdown-frontmatter')
 * @returns A disposable to unregister all providers
 */
export function registerReferenceProviders(
  monaco: Monaco,
  openTab: OpenTabFunction,
  language: string = 'markdown-frontmatter'
): ProviderRegistration {
  const disposables: IDisposable[] = [];

  // Register Document Link Provider (Ctrl+Click)
  disposables.push(
    monaco.languages.registerLinkProvider(
      language,
      createDocumentLinkProvider(monaco, openTab)
    )
  );

  // Register Hover Provider
  disposables.push(
    monaco.languages.registerHoverProvider(language, createHoverProvider(monaco))
  );

  // Register Definition Provider (F12)
  disposables.push(
    monaco.languages.registerDefinitionProvider(
      language,
      createDefinitionProvider(monaco, openTab)
    )
  );

  return {
    dispose: () => {
      disposables.forEach((d) => d.dispose());
    },
  };
}

// ============================================================================
// Individual exports for testing or selective registration
// ============================================================================

export {
  createDocumentLinkProvider,
  createHoverProvider,
  createDefinitionProvider,
};
